import {
  ActionIcon,
  Badge,
  Box,
  Flex,
  Modal,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconDots, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { ServiceRepairVoucherType } from "./serviceRepairVoucherDetail";

/**
 * Ô hiển thị mã phiếu kho trên bảng kê hàng dịch vụ.
 *
 * Một dòng bảng kê có thể đi qua cùng một chặng nhiều lần nên các cột mã phiếu
 * lưu nhiều mã nối bằng dấu phẩy. Ô này hiện mã đầu tiên, phần còn lại mở trong
 * modal có ô tìm kiếm — dùng modal thay tooltip để các mã đó cũng bấm xem được.
 *
 * Double-click vào mã bất kỳ để mở màn xem chi tiết phiếu.
 */
const ServiceRepairVoucherCodeCell = ({
  value,
  type,
  onOpenDetail,
  color = "blue",
  size,
}: {
  value: any;
  type: ServiceRepairVoucherType;
  onOpenDetail: (code: string, type: ServiceRepairVoucherType) => void;
  color?: string;
  size?: string;
}) => {
  const [opened, setOpened] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const values = value
    ? value
        .toString()
        .trim()
        .replace(/^,/, "")
        .split(",")
        .map((item: string) => item.trim())
        .filter((item: string) => item !== "")
    : [];

  if (values.length === 0) return null;

  const filteredValues = values.filter((item: string) =>
    item.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const openDetail = (code: string) => {
    setOpened(false);
    onOpenDetail(code, type);
  };

  return (
    <>
      <Box display="flex">
        <Flex align="center" gap={5}>
          <Tooltip label="Nhấn đúp để xem chi tiết phiếu">
            <Badge
              radius="sm"
              variant="outline"
              color={color}
              size={size}
              style={{ cursor: "pointer" }}
              onDoubleClick={() => openDetail(values[0])}
              fw={600}
            >
              {values[0]}
            </Badge>
          </Tooltip>
          {values.length > 1 && (
            <Tooltip label={`Xem ${values.length - 1} mã phiếu còn lại`}>
              <ActionIcon
                onClick={() => setOpened(true)}
                color="blue"
                radius="xl"
                variant="light"
                aria-label="Danh sách mã phiếu"
              >
                <IconDots size="1.2rem" />
              </ActionIcon>
            </Tooltip>
          )}
        </Flex>
      </Box>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        size="550"
        title={<Title order={5}>Danh sách mã phiếu</Title>}
      >
        <TextInput
          placeholder="Tìm kiếm mã phiếu..."
          value={searchTerm}
          radius="xl"
          mt={10}
          leftSection={<IconSearch size="1rem" />}
          onChange={(event) => setSearchTerm(event.currentTarget.value)}
          mb={10}
          w={170}
        />
        <Text size="xs" c="dimmed">
          Nhấn đúp vào mã phiếu để xem chi tiết.
        </Text>
        <Flex gap={10} mt={20} wrap="wrap">
          {filteredValues.map((item: string, index: number) => (
            <Badge
              radius="sm"
              variant="outline"
              color={color}
              size={size}
              key={index}
              style={{ cursor: "pointer" }}
              onDoubleClick={() => openDetail(item)}
              fw={600}
            >
              {item}
            </Badge>
          ))}
          {filteredValues.length === 0 && <Text>Không tìm thấy kết quả</Text>}
        </Flex>
      </Modal>
    </>
  );
};

export default ServiceRepairVoucherCodeCell;
